import {Component, computed, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {Mode} from '../../selection-zone/model/mode.enum';
import {UtilisateurSigalettService} from '../../../services/utilisateur-sigalett.service';
import {finalize} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {AuthService} from '../../../services/auth-service.service';
import {AlertService} from 'sigale-angular';
import {PreferencesUtilisateurDto} from './model/preferences-utilisateur.dto';
import {BureauPreferentielDto} from '../../../dto/bureau-preferentiel.dto';
import {isNotNullOrUndefined} from '@sigale-angular/src/utils/functions';
import {IlotService} from '../../../services/ilot.service';
import {ReservationService} from '../../../services/reservation.service';
import {Bureau} from '../../../../reserver-un-bureau/model/bureau.model';
import {IlotDto} from '../../../dto/ilot.dto';
import {Ilot} from '../../../../reserver-un-bureau/model/ilot.model';
import {UtilisateurSigalett} from '../../../models/utilisateur-sigalett.model';
import {MenuItem} from 'primeng/api';

@Component({
	selector: 'app-preferences',
	templateUrl: './preferences.component.html',
	styleUrl: './preferences.component.scss'
})
export class PreferencesComponent implements OnInit {
	readonly NOM_ALERTE_ERREUR_PREFERENCES = 'erreurPreferences';

	loadingPreferencesUtilisateur = signal(false);
	loadingSelectionZone = signal(false);
	loadingBureauPreferentiel = signal(false);
	loadingIlot = signal(false);

	ilot = signal<Ilot>(null);

	labelHaut = signal('');
	labelDroite = signal('');
	labelBas = signal('');
	labelGauche = signal('');

	zoneDeTravailParDefautId: WritableSignal<number> = signal(null);
	afficherWeekend = signal(false);

	activeIndex = signal(0);

	titreSection = computed(() => {
		switch (this.activeIndex()) {
			case 0: {
				return this.translateService.instant('PREFERENCES.TITRE_SECTION_CALENDRIER');
			}
			case 1: {
				return this.translateService.instant('PREFERENCES.TITRE_SECTION_RESERVATION');
			}
		}
	});

	utilisateurConnecte: UtilisateurSigalett;
	loginUtilisateurConnecte: string;
	bureauPreferentiel: BureauPreferentielDto | null = null;
	nomZoneDeTravail: string | null = null;
	nomIlot: string | null = null;
	couleurIlot: string | null = null;

	private utilisateurSigalettService = inject(UtilisateurSigalettService);
	private reservationService = inject(ReservationService);
	private ilotService = inject(IlotService);
	private authService = inject(AuthService);
	private alertService = inject(AlertService);
	private translateService = inject(TranslateService);

	items: MenuItem[] | undefined;

	ngOnInit(): void {
		this.utilisateurConnecte = this.authService.getUtilisateurConnecte();
		this.loginUtilisateurConnecte = this.utilisateurConnecte.login;
		this.rechercherPreferencesUtilisateur();
		this.rechercherBureauPreferentiel();
		this.items = [
			{
				label: 'Préférences',
				items: [
					{
						label: 'Calendrier',
						icon: 'pi pi-calendar',
						command: () => {
							this.activeIndex.set(0);
						},
						id: 'calendrier'
					},
					{
						label: 'Réservation',
						icon: 'pi pi-briefcase',
						command: () => {
							this.activeIndex.set(1);
						},
						id: 'reservation'
					}
				]
			}
		];
	}

	isActiveMenuItem(item: MenuItem): boolean {
		switch (this.activeIndex()) {
			case 0:
				if (item.id === 'calendrier') {
					return true;
				}
				break;
			case 1:
				if (item.id === 'reservation') {
					return true;
				}
				break;
		}

		return false;
	}

	/**
	 * Recherche les préférences de l'utilisateur
	 */
	rechercherPreferencesUtilisateur(): void {
		this.loadingPreferencesUtilisateur.set(true);
		this.utilisateurSigalettService.rechercherPreferencesUtilisateur(this.loginUtilisateurConnecte)
			.pipe(finalize(() => this.loadingPreferencesUtilisateur.set(false)))
			.subscribe({
				next: preferencesUtilisateurDto => {
					this.zoneDeTravailParDefautId.set(preferencesUtilisateurDto.zoneDeTravailParDefautId);
					this.afficherWeekend.set(preferencesUtilisateurDto.afficherWeekend);
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_PREFERENCES);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_PREFERENCES);
				}
			});
	}

	rechercherBureauPreferentiel(): void {
		this.loadingBureauPreferentiel.set(true);
		this.reservationService.recupererBureauPreferentielUtilisateurConnecte(this.loginUtilisateurConnecte)
			.pipe(finalize(() => this.loadingBureauPreferentiel.set(false)))
			.subscribe({
				next: bureauPreferentielDto => {
					this.bureauPreferentiel = bureauPreferentielDto;

					if (isNotNullOrUndefined(this.bureauPreferentiel)) {
						this.rechercherIlotAvecBureauPreferentiel();
					}
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_PREFERENCES);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_PREFERENCES);
				}
			});
	}

	rechercherIlotAvecBureauPreferentiel(): void {
		this.loadingIlot.set(true);
		this.ilotService.recupererIlotAvecBureauPreferentiel(this.bureauPreferentiel.id)
			.pipe(finalize(() => this.loadingIlot.set(false)))
			.subscribe({
				next: zoneDeTravailAvecIlotDto => {
					this.nomZoneDeTravail = zoneDeTravailAvecIlotDto.nom;
					this.nomIlot = zoneDeTravailAvecIlotDto.ilot.nom;
					this.couleurIlot = zoneDeTravailAvecIlotDto.ilot.couleur;

					const bureaux: Bureau[] = [];
					const ilot: IlotDto = zoneDeTravailAvecIlotDto.ilot;

					this.labelHaut.set(zoneDeTravailAvecIlotDto.indicateurHaut ?? '');
					this.labelDroite.set(zoneDeTravailAvecIlotDto.indicateurDroite ?? '');
					this.labelBas.set(zoneDeTravailAvecIlotDto.indicateurBas ?? '');
					this.labelGauche.set(zoneDeTravailAvecIlotDto.indicateurGauche ?? '');

					ilot.bureaux.forEach(bureauDto => {
						const bureau = new Bureau(this.translateService, bureauDto, bureauDto.coordonneeX, bureauDto.coordonneeY);

						bureaux.push(bureau);
					});

					this.ilot.set(new Ilot(ilot, bureaux));
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_PREFERENCES);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_PREFERENCES);
				}
			});
	}

	/**
	 * Modifie la zone par défaut en fonction de la valeur du dropdown.
	 * @param zoneSelectionneeId la nouvelle zone de travail par défaut.
	 */
	onZoneSelectionnee(zoneSelectionneeId?: number): void {
		this.enregistrerPreferencesUtilisateur(this.afficherWeekend(), zoneSelectionneeId);
	}

	protected readonly Mode = Mode;

	toggleAfficherWeekend(afficherWeekend: boolean): void {
		this.enregistrerPreferencesUtilisateur(afficherWeekend, this.zoneDeTravailParDefautId());
	}

	private enregistrerPreferencesUtilisateur(afficherWeekend: boolean, zoneSelectionneeId?: number): void {
		this.loadingSelectionZone.set(true);
		this.utilisateurSigalettService.enregistrerPreferences(this.loginUtilisateurConnecte, new PreferencesUtilisateurDto(afficherWeekend, zoneSelectionneeId))
			.pipe(finalize(() => this.loadingSelectionZone.set(false)))
			.subscribe({
				next: () => {
					this.zoneDeTravailParDefautId.set(zoneSelectionneeId);
					this.afficherWeekend.set(afficherWeekend);
				},
				error: error => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_PREFERENCES);
					this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_PREFERENCES);
				}
			});
	}

	protected readonly isNotNullOrUndefined = isNotNullOrUndefined;
}
